<script setup lang="ts">
import { z } from 'zod'
import { zodResolver } from '@primeuix/forms/resolvers/zod'
import { useToast } from '@/shared/composables/UseToast'
import { useDynamicDialog } from '@/shared/composables/UseDynamicDialog'
import { useDynamicDialogLifecycle } from '@/shared/composables/UseDynamicDialogLifecycle'
import EditSalesDailyLogDialog from '@/features/sales/dialogs/EditSalesDailyLogDialog.vue'
import SubscriptionInputSelect from '@/shared/components/form/SubscriptionInputSelect.vue'
import SalesDemoSourceFields from '@/features/sales/components/SalesDemoSourceFields.vue'
import {
  useAdminCreateSalesDailyLog,
  useAdminListMySalesDailyLogs,
  getAdminListSalesDailyLogsQueryKey,
  getAdminListMySalesDailyLogsQueryKey,
  getAdminGetSalesStatsQueryKey,
  getAdminGetMySalesStatsQueryKey,
  getAdminListSalesDemosQueryKey,
} from '@swipe4work/api-orval'
import type {
  AdminListMySalesDailyLogsParams,
  SalesDemoPotentialSubscriptionType,
  SalesDemoSource,
} from '@swipe4work/models-orval'
import { AdminPermissionResource, PermissionLevel } from '@swipe4work/models-orval'
import { useQueryClient } from '@tanstack/vue-query'

const emit = defineEmits<{
  (e: 'created'): void
}>()

const toast = useToast()
const dialog = useDynamicDialog()
const { closeDialog } = useDynamicDialogLifecycle()
const queryClient = useQueryClient()

const { mutateAsync: createLog, isPending } = useAdminCreateSalesDailyLog()

type DemoSubscriptionType = SalesDemoPotentialSubscriptionType | 'None'

interface DemoForm {
  companyName: string
  contactFirstName: string
  contactLastName: string
  contactEmail: string
  demoDate: Date | null
  potentialCloseDate: Date | null
  source: SalesDemoSource | null
  sourceOther: string
  subscriptionType: DemoSubscriptionType
  matchCredits: number | null
  subscriptionId: number | null
}

const date = ref<Date>(new Date())
const plannedDemos = ref<number | null>(null)
const demoForms = ref<DemoForm[]>([])

function toDateOnly(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/*
 * One log per (person, date): check live whether the chosen date already has a log, so the user can
 * jump to editing it instead of creating a duplicate.
 */
const existingLogParams = computed<AdminListMySalesDailyLogsParams>(() => ({
  dateFrom: toDateOnly(date.value) as unknown as Date,
  dateTo: toDateOnly(date.value) as unknown as Date,
  limit: 1,
}))
const { data: existingLogData, isPending: isCheckingExisting } = useAdminListMySalesDailyLogs(existingLogParams)
const existingLog = computed(() => existingLogData.value?.logs?.[0] ?? null)

// The planned-demos count drives an equal number of demo sub-forms below the log fields.
watch(plannedDemos, (count) => {
  const n = count ?? 0
  if (n > demoForms.value.length) {
    for (let i = demoForms.value.length; i < n; i++) {
      demoForms.value.push({
        companyName: '',
        contactFirstName: '',
        contactLastName: '',
        contactEmail: '',
        demoDate: null,
        potentialCloseDate: null,
        source: null,
        sourceOther: '',
        subscriptionType: 'None',
        matchCredits: null,
        subscriptionId: null,
      })
    }
  }
  else {
    demoForms.value = demoForms.value.slice(0, n)
  }
})

const baseSchema = z.object({
  calledCompanies: z.number({ required_error: 'Gebeld is verplicht' }).min(0, 'Minimaal 0'),
  conversations: z.number({ required_error: 'Gesprekken is verplicht' }).min(0, 'Minimaal 0'),
  plannedDemos: z.number({ required_error: 'Geplande demo\'s is verplicht' }).min(0, 'Minimaal 0'),
})

const resolver = zodResolver(baseSchema)

const subscriptionTypeOptions = [
  {
    label: 'Geen',
    value: 'None',
  },
  {
    label: 'Match Credits',
    value: 'MatchCredits',
  },
  {
    label: 'Vacature Slots',
    value: 'VacancySlots',
  },
]

const demoErrors = ref<Record<number, Record<string, string>>>({})
const hasTriedSubmit = ref(false)

function validateDemos(): boolean {
  demoErrors.value = {}
  let valid = true

  for (let i = 0; i < demoForms.value.length; i++) {
    const demo = demoForms.value[i]
    const errors: Record<string, string> = {}
    if (!demo.companyName.trim()) {
      errors.companyName = 'Bedrijfsnaam is verplicht'
      valid = false
    }
    if (demo.contactEmail.trim() && !/^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]+$/.test(demo.contactEmail)) {
      errors.contactEmail = 'Ongeldig e-mailadres'
      valid = false
    }
    if (!demo.source) {
      errors.source = 'Bron is verplicht'
      valid = false
    }
    if (demo.source === 'Other' && !demo.sourceOther.trim()) {
      errors.sourceOther = 'Vul in via welk kanaal de demo binnenkwam'
      valid = false
    }
    if (Object.keys(errors).length > 0) {
      demoErrors.value[i] = errors
    }
  }

  return valid
}

const dialogProps = {
  modal: true,
  closable: true,
  showHeader: false,
  dismissableMask: true,
  style: {
    width: 'auto',
    height: 'auto',
  },
}

function openEditExistingLogDialog() {
  if (!existingLog.value) return
  const logId = existingLog.value.id

  closeDialog()
  dialog.open(EditSalesDailyLogDialog, {
    props: dialogProps,
    data: { logId },
  })
}

async function onSubmit({ values, valid }: {
  values: any,
  valid: boolean
}) {
  hasTriedSubmit.value = true

  if (!valid || !values || existingLog.value) return

  const demosValid = validateDemos()
  if (!demosValid) return

  try {
    await createLog({
      data: {
        date: toDateOnly(date.value) as unknown as Date,
        calledCompanies: values.calledCompanies,
        conversations: values.conversations,
        plannedDemos: values.plannedDemos,
        demos: demoForms.value.map(d => ({
          companyName: d.companyName,
          contactFirstName: d.contactFirstName.trim() || null,
          contactLastName: d.contactLastName.trim() || null,
          contactEmail: d.contactEmail.trim() || null,
          demoDate: d.demoDate ?? null,
          potentialCloseDate: d.potentialCloseDate ?? null,
          // validateDemos already rejected a demo without a source.
          source: d.source ?? undefined,
          sourceOther: d.source === 'Other' ? d.sourceOther.trim() : null,
          potentialSubscriptionType: d.subscriptionType === 'None' ? undefined : d.subscriptionType,
          potentialSubscriptionId: d.subscriptionType === 'VacancySlots' ? d.subscriptionId : null,
          potentialMatchCredits: d.subscriptionType === 'MatchCredits' ? d.matchCredits : null,
        })),
      },
    })

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getAdminListSalesDailyLogsQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getAdminListMySalesDailyLogsQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getAdminGetSalesStatsQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getAdminGetMySalesStatsQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getAdminListSalesDemosQueryKey() }),
    ])

    toast.success('Log aangemaakt', 'Het sales log is succesvol aangemaakt')
    emit('created')
    closeDialog()
  }
  catch (error: any) {
    const detail = error?.response?.data?.detail
      ?? error?.data?.detail
      ?? 'Er is een fout opgetreden bij het aanmaken van het log'

    toast.error('Fout', detail)
  }
}
</script>

<template>
  <section class="max-h-screen min-w-lg flex flex-col gap-4 overflow-y-auto">
    <div class="mt-5 flex flex-col gap-1">
      <h2>
        Nieuwe Sales Log
      </h2>
      <p class="text-sm text-surface-500">
        Voer je dagelijkse sales activiteiten in.
      </p>
    </div>

    <Form :resolver="resolver" @submit="onSubmit" class="flex flex-col gap-4">
      <div class="grid grid-cols-2 gap-3">
        <div class="col-span-2 flex flex-col gap-1">
          <label for="logDate">Datum</label>
          <DatePicker
            v-model="date"
            input-id="logDate"
            date-format="dd-mm-yy"
            show-button-bar
            class="w-full"
          />
        </div>

        <Message v-if="existingLog" severity="warn" class="col-span-2">
          <div class="flex flex-col items-start gap-1">
            <span>Er bestaat al een log voor deze datum. Wil je die aanpassen?</span>
            <Button
              label="Bestaande log bewerken"
              variant="link"
              size="small"
              class="p-0!"
              @click="openEditExistingLogDialog"
            />
          </div>
        </Message>

        <FormField v-slot="$field" name="calledCompanies" class="flex flex-col gap-1">
          <label for="calledCompanies">Gebeld</label>
          <InputNumber
            v-bind="$field.props"
            input-id="calledCompanies"
            :min="0"
            :invalid="$field?.invalid"
            class="w-full"
          />
          <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">
            {{ $field.error?.message }}
          </Message>
        </FormField>

        <FormField v-slot="$field" name="conversations" class="flex flex-col gap-1">
          <label for="conversations">Gesprekken</label>
          <InputNumber
            v-bind="$field.props"
            input-id="conversations"
            :min="0"
            :invalid="$field?.invalid"
            class="w-full"
          />
          <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">
            {{ $field.error?.message }}
          </Message>
        </FormField>

        <FormField v-slot="$field" name="plannedDemos" class="col-span-2 flex flex-col gap-1">
          <label for="plannedDemos">Geplande Demo's</label>
          <InputNumber
            v-bind="$field.props"
            input-id="plannedDemos"
            :min="0"
            :invalid="$field?.invalid"
            class="w-full"
            @update:model-value="(v: number | null) => { plannedDemos = v }"
          />
          <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">
            {{ $field.error?.message }}
          </Message>
        </FormField>
      </div>

      <template v-if="demoForms.length > 0">
        <Divider />
        <div class="flex flex-col gap-6">
          <div
            v-for="(demo, index) in demoForms"
            :key="index"
            class="flex flex-col gap-3 border border-surface-200 rounded-lg p-4"
          >
            <h4 class="text-sm font-semibold">
              Demo {{ index + 1 }}
            </h4>

            <div class="grid grid-cols-2 gap-3">
              <div class="col-span-2 flex flex-col gap-1">
                <label :for="`companyName-${index}`">Bedrijfsnaam</label>
                <InputText
                  :id="`companyName-${index}`"
                  v-model="demo.companyName"
                  class="w-full"
                  :invalid="hasTriedSubmit && !!demoErrors[index]?.companyName"
                />
                <Message v-if="hasTriedSubmit && demoErrors[index]?.companyName" severity="error" size="small" variant="simple">
                  {{ demoErrors[index]?.companyName }}
                </Message>
              </div>

              <div class="flex flex-col gap-1">
                <label :for="`firstName-${index}`">Voornaam (optioneel)</label>
                <InputText
                  :id="`firstName-${index}`"
                  v-model="demo.contactFirstName"
                  class="w-full"
                />
              </div>

              <div class="flex flex-col gap-1">
                <label :for="`lastName-${index}`">Achternaam (optioneel)</label>
                <InputText
                  :id="`lastName-${index}`"
                  v-model="demo.contactLastName"
                  class="w-full"
                />
              </div>

              <div class="col-span-2 flex flex-col gap-1">
                <label :for="`contactEmail-${index}`">E-mailadres (optioneel)</label>
                <InputText
                  :id="`contactEmail-${index}`"
                  v-model="demo.contactEmail"
                  class="w-full"
                  :invalid="hasTriedSubmit && !!demoErrors[index]?.contactEmail"
                />
                <Message v-if="hasTriedSubmit && demoErrors[index]?.contactEmail" severity="error" size="small" variant="simple">
                  {{ demoErrors[index]?.contactEmail }}
                </Message>
              </div>

              <div class="flex flex-col gap-1">
                <label :for="`demoDate-${index}`">Demo datum (optioneel)</label>
                <DatePicker
                  v-model="demo.demoDate"
                  :input-id="`demoDate-${index}`"
                  date-format="dd-mm-yy"
                  show-button-bar
                  show-clear
                  class="w-full"
                />
              </div>

              <div class="flex flex-col gap-1">
                <label :for="`closeDate-${index}`">Potentiële onboarding datum (optioneel)</label>
                <DatePicker
                  v-model="demo.potentialCloseDate"
                  :input-id="`closeDate-${index}`"
                  date-format="dd-mm-yy"
                  show-button-bar
                  show-clear
                  class="w-full"
                />
              </div>

              <SalesDemoSourceFields
                v-model:source="demo.source"
                v-model:source-other="demo.sourceOther"
                :id-suffix="String(index)"
                :source-error="hasTriedSubmit ? demoErrors[index]?.source : undefined"
                :source-other-error="hasTriedSubmit ? demoErrors[index]?.sourceOther : undefined"
              />

              <div class="col-span-2 flex flex-col gap-1">
                <label :for="`subType-${index}`">Potentieel abonnement</label>
                <SelectButton
                  v-model="demo.subscriptionType"
                  :input-id="`subType-${index}`"
                  :options="subscriptionTypeOptions"
                  option-label="label"
                  option-value="value"
                  class="w-full"
                />
              </div>

              <template v-if="demo.subscriptionType === 'MatchCredits'">
                <div class="col-span-2 flex flex-col gap-1">
                  <label :for="`matchCredits-${index}`">Aantal match credits</label>
                  <InputNumber
                    v-model="demo.matchCredits"
                    :input-id="`matchCredits-${index}`"
                    :min="0"
                    class="w-full"
                  />
                </div>
              </template>

              <template v-if="demo.subscriptionType === 'VacancySlots'">
                <div class="col-span-2 flex flex-col gap-1">
                  <label :for="`subscription-${index}`">Abonnement</label>
                  <SubscriptionInputSelect
                    v-model="demo.subscriptionId"
                    :input-id="`subscription-${index}`"
                  />
                </div>
              </template>
            </div>
          </div>
        </div>
      </template>

      <div class="mt-2 flex justify-end gap-2">
        <Button label="Annuleren" severity="secondary" :disabled="isPending" @click="closeDialog" />
        <Button
          v-permission="{ resource: AdminPermissionResource.SalesMetrics, level: PermissionLevel.Write }"
          type="submit"
          label="Opslaan"
          :loading="isPending"
          :disabled="isCheckingExisting || !!existingLog"
        />
      </div>
    </Form>
  </section>
</template>
